export const japaneseHumanoidRobotsPage = {
  "path": "/humanoid-robots/japan",
  "title": "Japanese Humanoid Robots Japan’s Humanoid Robotics Legacy and Current Research",
  "seoTitle": "Japanese Humanoid Robots ASIMO, Toyota T-HR3, HRP-5P, WABOT, QRIO and More",
  "seoDescription": "Explore Japan’s humanoid robotics ecosystem, from Waseda’s WABOT and Honda’s ASIMO to Toyota’s T-HR3, AIST’s HRP robots, Kawasaki’s RHP platforms, Musashi, QRIO, Actroid and Geminoid.",
  "description": "Japan’s humanoid research spans Waseda’s early WABOT projects and Honda’s ASIMO to Toyota’s T-HR3, AIST’s HRP platforms, Kawasaki’s RHP robots and University of Tokyo’s musculoskeletal humanoids.",
  "heroText": "Japan’s humanoid robotics story is not one single robot. It is a long chain of bipedal walking research, human-robot interaction, teleoperation, android design, assistive robotics and industrial manipulation. The country produced some of the most important humanoid platforms in robotics history, including WABOT-1, ASIMO, QRIO, HRP-2, HRP-4C and Toyota T-HR3. Today, Japanese labs and companies continue to explore humanoid robots for nursing support, construction-style manipulation, disaster response, telepresence and embodied AI research.",
  "updatedAt": "July 8, 2026",
  "author": "TechniaHQRobot Research Desk",
  "readingTime": "18 min read",
  "keywords": [
    "Japanese humanoid robots",
    "humanoid robots in Japan",
    "Japan robotics",
    "ASIMO",
    "Toyota T-HR3",
    "HRP-5P",
    "HRP-2",
    "WABOT-1",
    "QRIO",
    "Musashi robot",
    "Kawasaki Kaleido",
    "RHP Friends",
    "Actroid",
    "Geminoid",
    "Pepper robot",
    "Honda humanoid robot",
    "Toyota humanoid robot",
    "AIST humanoid robot",
    "Waseda humanoid robot"
  ],
  "summaryCards": [
    {
      "label": "Historical icons",
      "value": "3",
      "detail": "WABOT-1, ASIMO and QRIO made Japanese humanoid robotics visible far beyond research labs."
    },
    {
      "label": "Industrial and research platforms",
      "value": "HRP + RHP",
      "detail": "AIST, Kawada and Kawasaki connect bipedal research to manipulation, construction-style tasks and whole-body control."
    },
    {
      "label": "Teleoperation and service",
      "value": "2+",
      "detail": "Toyota T-HR3 and RHP Friends show why remote operation still matters for safe assistance and nursing contexts."
    },
    {
      "label": "Android and social robots",
      "value": "4",
      "detail": "HRP-4C, Actroid, Geminoid and Pepper belong to the interaction side of Japan’s humanoid story."
    },
    {
      "label": "University research",
      "value": "Waseda + Tokyo",
      "detail": "WABOT, WABIAN, KOBIAN, Musashi and Musashi-W show the depth of university-led humanoid research."
    }
  ],
  "robots": [
    {
      "id": "asimo",
      "name": "ASIMO",
      "organization": "Honda",
      "region": "Tokyo / Wako R&D legacy",
      "status": "Historical, retired",
      "statusTone": "historical",
      "years": "Introduced 2000; retired 2022",
      "mainUse": "Humanoid mobility, interaction and technology demonstration.",
      "mobility": "Bipedal humanoid",
      "keyHardware": "Compact bipedal body with legs, arms, hands and perception systems used for walking, stairs, running-style motion and human-facing demonstrations.",
      "sourceLabel": "Honda ASIMO official",
      "sourceUrl": "https://global.honda/en/ASIMO/",
      "overview": "ASIMO became one of the most recognizable humanoid robots in the world because Honda used it to show compact bipedal walking, balance, stair climbing, running-style demonstrations and friendly human interaction. It was a research and demonstration platform, not a factory worker.",
      "technicalNotes": "Honda describes ASIMO as a platform that helped the company study movement in shared spaces, hand tasks and interaction with people. Honda also states that fully autonomous bipedal robots for living environments still require long-term research, safety work, social agreement and regulation.",
      "whyItMatters": "ASIMO gave Japan a global symbol for bipedal robotics. Its importance is the engineering knowledge Honda accumulated around motion, falls, people nearby and the limits of autonomy in real environments."
    },
    {
      "id": "honda-e-p-series",
      "name": "Honda E-Series and P-Series",
      "organization": "Honda",
      "region": "Wako / Honda R&D",
      "status": "Historical prototypes",
      "statusTone": "historical",
      "years": "1986 onward for E-Series; 1990s for P-Series",
      "mainUse": "Early bipedal walking and humanoid body research before ASIMO.",
      "mobility": "Bipedal prototypes",
      "keyHardware": "Experimental legged and full-body prototypes used to build Honda’s base knowledge in bipedal locomotion.",
      "sourceLabel": "Honda robotics history",
      "sourceUrl": "https://global.honda/en/ASIMO/",
      "overview": "Honda’s E-Series and P-Series form the engineering path that led to ASIMO. They are less famous than ASIMO but essential for understanding Japan’s sustained expertise in bipedal walking.",
      "technicalNotes": "These robots should be labeled as research prototypes. They tested walking mechanics, body scale and control ideas across multiple generations before Honda introduced ASIMO in 2000.",
      "whyItMatters": "They show the long time horizon of serious humanoid work. Japan’s humanoid history is controlled iteration across decades, not a set of isolated viral demos."
    },
    {
      "id": "toyota-t-hr3",
      "name": "Toyota T-HR3",
      "organization": "Toyota",
      "region": "Aichi / Toyota City",
      "status": "Prototype / research",
      "statusTone": "prototype",
      "years": "Introduced 2017",
      "mainUse": "Teleoperation, remote assistance and humanoid control research.",
      "mobility": "Humanoid platform controlled through a master maneuvering system",
      "keyHardware": "Humanoid body designed around remote whole-body control by a human operator through Toyota’s master maneuvering interface.",
      "sourceLabel": "Toyota T-HR3 announcement",
      "sourceUrl": "https://global.toyota/en/detail/201703",
      "overview": "Toyota T-HR3 is one of Japan’s clearest examples of a humanoid built around remote operation. A human operator controls the robot’s body through a master maneuvering system.",
      "technicalNotes": "T-HR3 should be presented as a teleoperated humanoid research platform. Its value comes from mapping operator motion and force feedback into a humanoid body, not from independent factory deployment.",
      "whyItMatters": "T-HR3 keeps the human in the loop. Difficult humanoid tasks often need supervision, force awareness and remote intervention long before full independence is realistic."
    },
    {
      "id": "toyota-partner-robot",
      "name": "Toyota Partner Robot",
      "organization": "Toyota",
      "region": "Aichi / Toyota City",
      "status": "Historical prototype family",
      "statusTone": "historical",
      "years": "Expo 2005 era",
      "mainUse": "Service, musical performance and partner robot demonstrations.",
      "mobility": "Bipedal and wheeled variants depending on version",
      "keyHardware": "Humanoid and service robot prototypes including walking, rolling and instrument-playing demonstration variants.",
      "sourceLabel": "Toyota Partner Robot archive",
      "sourceUrl": "http://www.toyota.co.jp/en/news/04/1203_1d.html",
      "overview": "Toyota’s Partner Robot program became visible around Expo 2005 with robots that played instruments and showed different mobility approaches.",
      "technicalNotes": "The Partner Robot line should be treated as a prototype family. It explored human-facing robots and service contexts but did not become a mass-market general-purpose humanoid.",
      "whyItMatters": "The project shows Toyota’s long-running interest in robots that assist people and explains why later Toyota humanoid work emphasized careful control and teleoperation."
    },
    {
      "id": "hrp-2",
      "name": "HRP-2",
      "organization": "AIST / Kawada Industries",
      "region": "Tsukuba / industrial research network",
      "status": "Research legacy platform",
      "statusTone": "research",
      "years": "Early 2000s",
      "mainUse": "Bipedal locomotion, whole-body control and humanoid manipulation research.",
      "mobility": "Bipedal humanoid research platform",
      "keyHardware": "Full-size humanoid platform in the HRP series, used by research labs for walking, contact planning, manipulation and control studies.",
      "sourceLabel": "Kawada HRP archive",
      "sourceUrl": "https://web.archive.org/web/20060209100455/http://www.kawada.co.jp/global/ams/hrp_2.html",
      "overview": "HRP-2, also known as Promet in the HRP family, is one of Japan’s best-known humanoid research platforms.",
      "technicalNotes": "HRP-2 is a serious research platform, not a consumer robot. It supported experiments in bipedal movement, manipulation, contacts and software architecture.",
      "whyItMatters": "HRP-2 gave labs a shared humanoid body that could be tested, repaired and improved. That made repeatable humanoid research easier."
    },
    {
      "id": "hrp-3",
      "name": "HRP-3",
      "organization": "AIST / Kawada Industries",
      "region": "Tsukuba / Kawada research network",
      "status": "Research legacy platform",
      "statusTone": "research",
      "years": "2000s",
      "mainUse": "Humanoid research for harsher environments and field-style operation.",
      "mobility": "Bipedal humanoid research platform",
      "keyHardware": "HRP-series humanoid body designed for research on walking, manipulation and operation in more demanding environments than clean lab demos.",
      "sourceLabel": "HRP project reference",
      "sourceUrl": "https://en.wikipedia.org/wiki/Humanoid_Robotics_Project",
      "overview": "HRP-3 extended the HRP series toward harder environments and blue-collar style robotics research.",
      "technicalNotes": "The page treats HRP-3 as a legacy research platform. It is included for continuity in the HRP sequence, not as a current commercial humanoid worker.",
      "whyItMatters": "HRP-3 matters because humanoid usefulness depends on environment, contact, balance margins and task conditions that are less forgiving than a stage demo."
    },
    {
      "id": "hrp-4",
      "name": "HRP-4",
      "organization": "AIST / Kawada Industries",
      "region": "Tsukuba / Kawada research network",
      "status": "Research legacy platform",
      "statusTone": "research",
      "years": "2010 era",
      "mainUse": "Slim bipedal humanoid research and whole-body control.",
      "mobility": "Bipedal humanoid research platform",
      "keyHardware": "Slimmer HRP platform used for humanoid motion, manipulation and control research.",
      "sourceLabel": "HRP-4 research reference",
      "sourceUrl": "https://arxiv.org/abs/1809.07073",
      "overview": "HRP-4 continued the HRP line with a slimmer humanoid platform for research.",
      "technicalNotes": "Research use around stair climbing and whole-body control shows HRP-4’s value in practical experiments, not mass deployment.",
      "whyItMatters": "HRP-4 shows the bridge between robot hardware and robot software. A body becomes meaningful only when control and stabilization survive real experiments."
    },
    {
      "id": "hrp-4c-miim",
      "name": "HRP-4C / Miim",
      "organization": "AIST",
      "region": "Tsukuba",
      "status": "Research legacy platform",
      "statusTone": "research",
      "years": "Introduced 2009",
      "mainUse": "Expression, speech, walking and performance research.",
      "mobility": "Bipedal female-form humanoid research platform",
      "keyHardware": "Human-like head and body form with body motors and dedicated facial expression actuation reported by AIST-related materials.",
      "sourceLabel": "AIST HRP-4C archive",
      "sourceUrl": "https://web.archive.org/web/20110323164504/http://www.aist.go.jp/aist_e/latest_research/2009/20090513/20090513.html",
      "overview": "HRP-4C, nicknamed Miim, brought the HRP line into human-like appearance and performance research.",
      "technicalNotes": "HRP-4C should not be confused with a service worker. It was a cybernetic human platform for expression, motion and performance research.",
      "whyItMatters": "Miim matters because Japan’s humanoid work was never only about walking. It also studied appearance, expression and how people perceive robots."
    },
    {
      "id": "hrp-5p",
      "name": "HRP-5P",
      "organization": "AIST / Kawada Industries",
      "region": "Tsukuba / Kawada research network",
      "status": "Research platform",
      "statusTone": "research",
      "years": "Late 2010s onward",
      "mainUse": "Construction-style manipulation, bipedal locomotion and whole-body research.",
      "mobility": "Large bipedal humanoid research platform",
      "keyHardware": "Life-size humanoid platform used in research on bipedal walking, manipulation and construction-style tasks.",
      "sourceLabel": "HRP-5P walking research",
      "sourceUrl": "https://arxiv.org/abs/2207.12644",
      "overview": "HRP-5P is one of the most important later HRP systems because it points toward larger humanoid work moving, reaching, manipulating materials and testing locomotion under demanding conditions.",
      "technicalNotes": "HRP-5P is a research platform. It should not be presented as a commercial construction worker already deployed at scale.",
      "whyItMatters": "Construction-style manipulation exposes hard problems balance, payload, perception, planning, foot placement and contact with the environment."
    },
    {
      "id": "rhp-kaleido",
      "name": "RHP Kaleido",
      "organization": "Kawasaki Heavy Industries / AIST ecosystem",
      "region": "Kawasaki / Tsukuba research ecosystem",
      "status": "Active or recent research",
      "statusTone": "active",
      "years": "2020s",
      "mainUse": "Humanoid whole-body contact, tactile sensing and manipulation research.",
      "mobility": "Life-size bipedal humanoid research platform",
      "keyHardware": "Humanoid research body used in papers on whole-body contact, tactile sensing and multi-contact manipulation.",
      "sourceLabel": "RHP Kaleido tactile paper",
      "sourceUrl": "https://arxiv.org/abs/2506.15146",
      "overview": "RHP Kaleido represents a newer wave of Japanese humanoid research around contact-rich movement.",
      "technicalNotes": "Recent papers describe RHP7 Kaleido in experiments with tactile modalities and whole-body contact manipulation. The page describes it as a research platform, not a deployed worker.",
      "whyItMatters": "Humanoids in real spaces touch walls, furniture, floors and objects. Kaleido matters because tactile feedback and whole-body contact are closer to that reality than a clean walking demo."
    },
    {
      "id": "rhp-friends",
      "name": "RHP Friends",
      "organization": "Kawasaki Heavy Industries / AIST ecosystem",
      "region": "Kawasaki / Tsukuba research ecosystem",
      "status": "Active or recent research",
      "statusTone": "active",
      "years": "2020s",
      "mainUse": "Nursing assistance research with autonomous and teleoperated task modes.",
      "mobility": "Slim humanoid research platform for human-coexisting spaces",
      "keyHardware": "Humanoid platform described in nursing-context research with locomotion, manipulation, teleoperation and object detection/tracking.",
      "sourceLabel": "RHP Friends paper",
      "sourceUrl": "https://arxiv.org/abs/2412.20770",
      "overview": "RHP Friends is a recent humanoid research platform aimed at assistive tasks in environments designed for humans.",
      "technicalNotes": "Research papers show nursing-context demonstrations and a mix of autonomy and teleoperation, not confirmed mass deployment.",
      "whyItMatters": "It matters because care environments need autonomy for repeated tasks and remote human assistance for unusual or safety-critical moments."
    },
    {
      "id": "musashi",
      "name": "Musashi",
      "organization": "University of Tokyo / JSK Lab",
      "region": "Tokyo",
      "status": "Active or recent research",
      "statusTone": "active",
      "years": "2020s",
      "mainUse": "Musculoskeletal humanoid research, learning control and human-like body mechanics.",
      "mobility": "Musculoskeletal humanoid research platform",
      "keyHardware": "Flexible musculoskeletal body concept with redundant sensors, muscle-like actuation routes and learning-control experiments described in research papers.",
      "sourceLabel": "Musashi platform paper",
      "sourceUrl": "https://arxiv.org/abs/2410.22000",
      "overview": "Musashi is part of the University of Tokyo / JSK Lab line of musculoskeletal humanoids. The design focuses on human-like mechanical structure and learning-based control.",
      "technicalNotes": "Musashi is a laboratory platform. Its value is in studying musculoskeletal design and learning control, not commercial deployment.",
      "whyItMatters": "Most humanoids use rigid links and conventional actuators. Musashi tests whether robot bodies can become more flexible, redundant and human-like in how they generate motion."
    },
    {
      "id": "musashi-w",
      "name": "Musashi-W",
      "organization": "University of Tokyo / JSK Lab",
      "region": "Tokyo",
      "status": "Active or recent research",
      "statusTone": "active",
      "years": "2020s",
      "mainUse": "Wheeled musculoskeletal humanoid research for real-world manipulation tasks.",
      "mobility": "Wheeled base with musculoskeletal upper body",
      "keyHardware": "Wheeled base combined with musculoskeletal upper limbs, static/dynamic body schema learning, reflex control and visual recognition in research description.",
      "sourceLabel": "Musashi-W paper",
      "sourceUrl": "https://arxiv.org/abs/2403.11729",
      "overview": "Musashi-W is the wheeled branch of the Musashi research direction. It keeps the musculoskeletal upper body and adds a mobile base for more practical task experiments.",
      "technicalNotes": "Musashi-W avoids the full burden of bipedal walking by using wheels, while preserving the upper-body research problem.",
      "whyItMatters": "Many useful humanoid tasks do not require legs. A wheeled humanoid upper body can test cleaning, carrying, table setting and manipulation while avoiding some bipedal stability costs."
    },
    {
      "id": "wabot-1",
      "name": "WABOT-1",
      "organization": "Waseda University",
      "region": "Tokyo / Waseda",
      "status": "Historical icon",
      "statusTone": "historical",
      "years": "1970s",
      "mainUse": "Early full-scale humanoid robotics research.",
      "mobility": "Bipedal / full-scale humanoid research system",
      "keyHardware": "Early humanoid system reported with walking, Japanese communication, external perception and hand-based object handling in historical robotics accounts.",
      "sourceLabel": "Waseda WABOT history",
      "sourceUrl": "https://www.humanoid.waseda.ac.jp/booklet/kato_2-j.html",
      "overview": "WABOT-1 belongs at the start of the modern Japanese humanoid story. Waseda’s work made the idea of a full-scale humanoid a concrete research project.",
      "technicalNotes": "WABOT-1 is a pioneering historical system. It is not active hardware for current deployment.",
      "whyItMatters": "It matters because later Japanese humanoids inherited the same grand problem combine legs, arms, perception, speech and control into one body for human spaces."
    },
    {
      "id": "wabot-2",
      "name": "WABOT-2",
      "organization": "Waseda University",
      "region": "Tokyo / Waseda",
      "status": "Historical icon",
      "statusTone": "historical",
      "years": "1980s",
      "mainUse": "Humanoid musician robot and human-robot interaction research.",
      "mobility": "Static / humanoid musician platform",
      "keyHardware": "Humanoid robot musician research platform described as reading a score and playing an electronic organ in historical accounts.",
      "sourceLabel": "Waseda WABOT history",
      "sourceUrl": "https://www.humanoid.waseda.ac.jp/booklet/kato_2-j.html",
      "overview": "WABOT-2 moved Waseda’s humanoid work toward playing music and symbolic action.",
      "technicalNotes": "WABOT-2 expanded humanoid robotics from walking and manipulation into music, timing and interaction.",
      "whyItMatters": "It matters because Japanese humanoid robotics often mixes engineering with culture. WABOT-2 linked machine control to performance."
    },
    {
      "id": "wabian",
      "name": "WABIAN / WABIAN-2R",
      "organization": "Waseda University",
      "region": "Tokyo / Waseda",
      "status": "Research legacy platform",
      "statusTone": "research",
      "years": "1990s to 2000s",
      "mainUse": "Human-size bipedal walking and gait research.",
      "mobility": "Bipedal humanoid research platform",
      "keyHardware": "Human-size walking humanoid platform used by Waseda for gait, posture and biped locomotion studies.",
      "sourceLabel": "Waseda humanoid robotics",
      "sourceUrl": "https://www.humanoid.waseda.ac.jp/booklet/kato_2-j.html",
      "overview": "WABIAN and WABIAN-2R represent Waseda’s mature work on human-size bipedal walking after WABOT.",
      "technicalNotes": "WABIAN is a research platform lineage. Present it as bipedal locomotion research, not as a deployed service robot.",
      "whyItMatters": "Bipedal walking remains hard. WABIAN belongs in any serious timeline of Japanese walking research."
    },
    {
      "id": "kobian",
      "name": "KOBIAN / KOBIAN-R",
      "organization": "Waseda University",
      "region": "Tokyo / Waseda",
      "status": "Research legacy platform",
      "statusTone": "research",
      "years": "2000s",
      "mainUse": "Expressive humanoid research and culture-specific greetings.",
      "mobility": "Bipedal expressive humanoid research platform",
      "keyHardware": "Humanoid body with expressive face/head system derived from Waseda humanoid and emotional-expression research lines.",
      "sourceLabel": "KOBIAN research reference",
      "sourceUrl": "https://en.wikipedia.org/wiki/Kobian",
      "overview": "KOBIAN connected Waseda’s walking humanoid work to expressive interaction.",
      "technicalNotes": "KOBIAN should be framed as human-robot interaction research. The interesting point is emotion expression and social signaling, not industrial labor.",
      "whyItMatters": "Humanoid acceptance depends on more than motion. Expression, greeting and timing affect how a person reads the robot."
    },
    {
      "id": "qrio",
      "name": "QRIO",
      "organization": "Sony",
      "region": "Tokyo / Sony research legacy",
      "status": "Discontinued historical prototype",
      "statusTone": "discontinued",
      "years": "Early 2000s; discontinued 2006",
      "mainUse": "Small bipedal entertainment humanoid and corporate technology demonstrator.",
      "mobility": "Small bipedal humanoid",
      "keyHardware": "Compact bipedal entertainment robot known for dance, running-style demonstrations and interaction experiments.",
      "sourceLabel": "QRIO source reference",
      "sourceUrl": "https://en.wikipedia.org/wiki/QRIO",
      "overview": "Sony’s QRIO was a small bipedal entertainment humanoid that followed the AIBO era.",
      "technicalNotes": "QRIO was never a mass-market humanoid product. It should be labeled as discontinued and historical.",
      "whyItMatters": "QRIO showed that humanoids could become public technology icons. Its discontinuation also shows that visibility is not durable market fit."
    },
    {
      "id": "hoap-series",
      "name": "HOAP-1 / HOAP-2 / HOAP-3",
      "organization": "Fujitsu",
      "region": "Japan / Fujitsu research legacy",
      "status": "Research legacy platform",
      "statusTone": "research",
      "years": "2001 to 2005 era",
      "mainUse": "Small humanoid research platforms for open architecture experiments.",
      "mobility": "Small bipedal humanoid research platforms",
      "keyHardware": "Humanoid for Open Architecture Platform series used by researchers for software, control and motion experiments.",
      "sourceLabel": "HOAP source reference",
      "sourceUrl": "https://en.wikipedia.org/wiki/HOAP",
      "overview": "Fujitsu’s HOAP series gave researchers smaller humanoid bodies for open-architecture work.",
      "technicalNotes": "HOAP should be separated from HRP. It was a small research platform family, useful for labs, not a service humanoid deployment.",
      "whyItMatters": "Full-size humanoids are expensive and difficult to maintain. Small platforms helped more teams test walking, control and perception."
    },
    {
      "id": "actroid",
      "name": "Actroid",
      "organization": "Osaka University / Kokoro",
      "region": "Osaka / Tokyo exhibition network",
      "status": "Android research and exhibition platform",
      "statusTone": "research",
      "years": "Introduced 2003; Expo 2005 era",
      "mainUse": "Realistic android appearance, speech and human-facing interaction research.",
      "mobility": "Android / largely stationary or limited-motion human-like platform depending on version",
      "keyHardware": "Realistic human-like head and upper-body actuation, speech, blinking and exhibition-oriented interaction in reported versions.",
      "sourceLabel": "Actroid reference",
      "sourceUrl": "https://en.wikipedia.org/wiki/Actroid",
      "overview": "Actroid is part of Japan’s realistic android branch. Developed through Osaka University and Kokoro, it pushed visual human-likeness, facial movement and exhibition interaction.",
      "technicalNotes": "Actroid is important for android design and interaction. It should not be presented as a mobile industrial humanoid worker.",
      "whyItMatters": "A realistic face changes expectations, trust, discomfort and the way people interpret machine behavior."
    },
    {
      "id": "geminoid-hi-1",
      "name": "Geminoid HI-1",
      "organization": "Hiroshi Ishiguro Lab / ATR / Osaka University",
      "region": "Osaka / Kyoto region",
      "status": "Teleoperated android research platform",
      "statusTone": "research",
      "years": "2000s",
      "mainUse": "Telepresence, android identity and human-robot interaction research.",
      "mobility": "Teleoperated android / static interaction platform",
      "keyHardware": "Human-like android body modeled on Hiroshi Ishiguro, used for remote presence and interaction research.",
      "sourceLabel": "Geminoid project",
      "sourceUrl": "http://www.geminoid.jp/en/index.html",
      "overview": "Geminoid HI-1 is one of the most famous android doubles in robotics. It explored what happens when a teleoperated machine looks like a specific person.",
      "technicalNotes": "Geminoid HI-1 should be described as teleoperated android research. It is not an autonomous mobile humanoid worker.",
      "whyItMatters": "It separates body realism from autonomy. A robot can feel socially powerful without being physically independent or useful as a worker."
    },
    {
      "id": "s-one-schaft",
      "name": "S-One",
      "organization": "SCHAFT",
      "region": "Tokyo / Japan-origin startup legacy",
      "status": "Historical prototype / discontinued company lineage",
      "statusTone": "historical",
      "years": "DARPA Robotics Challenge era",
      "mainUse": "Disaster-response style humanoid robotics competition platform.",
      "mobility": "Bipedal humanoid competition robot",
      "keyHardware": "Humanoid platform known for strong performance in DARPA Robotics Challenge qualification context.",
      "sourceLabel": "DARPA SCHAFT reference",
      "sourceUrl": "https://en.wikipedia.org/wiki/SCHAFT",
      "overview": "S-One brought Japanese humanoid capability into the global disaster-response robotics conversation during the DARPA Robotics Challenge era.",
      "technicalNotes": "Public current-product information is limited, so it is labeled as historical rather than active.",
      "whyItMatters": "Disaster robotics forced humanoids into hard tasks valves, vehicles, tools, doors, debris and operator interfaces."
    },
    {
      "id": "pepper",
      "name": "Pepper",
      "organization": "SoftBank Robotics / Aldebaran",
      "region": "Japan-linked; French-origin platform",
      "status": "Japan-linked, production stopped",
      "statusTone": "japanLinked",
      "years": "Introduced 2014; production stopped later",
      "mainUse": "Social robotics, customer interaction, education and public-facing demonstrations.",
      "mobility": "Wheeled semi-humanoid service robot",
      "keyHardware": "Upper-body humanoid form with arms, screen interface and wheeled base. The Japan link comes through SoftBank ownership and public deployment, while original development came from Aldebaran in France.",
      "sourceLabel": "SoftBank Robotics Europe reference",
      "sourceUrl": "https://www.aldebaran.com/en/pepper",
      "overview": "Pepper became one of the most visible social humanoids in Japan and abroad. Its body was built for interaction rather than industrial manipulation.",
      "technicalNotes": "Pepper must be labeled carefully. It is strongly linked to Japan through SoftBank, but it is not a purely Japanese-origin humanoid platform.",
      "whyItMatters": "Pepper tested whether humanoids could work as greeters, classroom companions and customer-facing robots. Its production stop is a reminder that visibility is not durable market fit."
    }
  ],
  "hubs": [
    {
      "flag": "🇯🇵",
      "city": "Tokyo",
      "detail": "Tokyo anchors JSK Lab’s Musashi work, Honda demonstrations, Sony and SoftBank robotics history and several Japan-wide robotics networks.",
      "robots": [
        "Musashi",
        "Musashi-W",
        "ASIMO",
        "QRIO",
        "Pepper"
      ]
    },
    {
      "flag": "🇯🇵",
      "city": "Waseda / Tokyo area",
      "detail": "Waseda is essential to the historical humanoid timeline through WABOT-1, WABOT-2, WABIAN and KOBIAN.",
      "robots": [
        "WABOT-1",
        "WABOT-2",
        "WABIAN",
        "KOBIAN"
      ]
    },
    {
      "flag": "🇯🇵",
      "city": "Tsukuba",
      "detail": "Tsukuba concentrates AIST humanoid research and the HRP lineage from HRP-2 to HRP-5P and HRP-4C.",
      "robots": [
        "HRP-2",
        "HRP-4C",
        "HRP-5P"
      ]
    },
    {
      "flag": "🇯🇵",
      "city": "Aichi / Toyota City",
      "detail": "Toyota’s humanoid and partner robot work connects the Aichi industrial ecosystem to teleoperation and service robotics.",
      "robots": [
        "Toyota Partner Robot",
        "Toyota T-HR3"
      ]
    },
    {
      "flag": "🇯🇵",
      "city": "Osaka / Kyoto region",
      "detail": "Osaka University, ATR and the Hiroshi Ishiguro Lab ecosystem are central to androids, telepresence and human-likeness research.",
      "robots": [
        "Actroid",
        "Geminoid HI-1"
      ]
    },
    {
      "flag": "🇯🇵",
      "city": "Kawasaki / industrial robotics region",
      "detail": "Kawasaki and AIST-linked research connect humanoid hardware to whole-body contact, nursing assistance and tactile robotics.",
      "robots": [
        "RHP Kaleido",
        "RHP Friends"
      ]
    },
    {
      "flag": "🇯🇵",
      "city": "Sony / Fujitsu historical robotics",
      "detail": "Sony’s QRIO and Fujitsu’s HOAP platforms show Japan’s early 2000s push into small humanoids for entertainment and research.",
      "robots": [
        "QRIO",
        "HOAP"
      ]
    }
  ],
  "timeline": [
    {
      "label": "1970s",
      "detail": "Waseda develops WABOT-1, one of the earliest full-scale humanoid robotics projects."
    },
    {
      "label": "1980s",
      "detail": "Waseda develops WABOT-2, a humanoid robot musician that connects robotics to symbolic action and performance."
    },
    {
      "label": "1986 onward",
      "detail": "Honda begins the E-Series bipedal robot program, building the walking research base that later leads to ASIMO."
    },
    {
      "label": "1990s",
      "detail": "Honda develops the P-Series and Waseda continues human-size biped research through WABIAN."
    },
    {
      "label": "2000",
      "detail": "Honda introduces ASIMO and gives Japanese humanoid robotics a global public icon."
    },
    {
      "label": "2001 to 2005",
      "detail": "Fujitsu develops HOAP humanoid research robots for smaller open-architecture experiments."
    },
    {
      "label": "Early 2000s",
      "detail": "Sony develops QRIO, and AIST/Kawada expand the HRP humanoid research series."
    },
    {
      "label": "2003 onward",
      "detail": "Actroid and Geminoid projects push Japan’s android research into realism, telepresence and human-robot interaction."
    },
    {
      "label": "2010s",
      "detail": "Toyota develops T-HR3 and partner robot research around remote operation, service and assistance."
    },
    {
      "label": "2020s",
      "detail": "HRP-5P, RHP Kaleido, RHP Friends, Musashi and Musashi-W continue Japanese humanoid research around contact, assistance, manipulation and musculoskeletal control."
    }
  ],
  "classifications": [
    {
      "label": "Active or recent research",
      "robots": [
        "Toyota T-HR3",
        "HRP-5P",
        "RHP Kaleido",
        "RHP Friends",
        "Musashi",
        "Musashi-W"
      ]
    },
    {
      "label": "Historical icons",
      "robots": [
        "WABOT-1",
        "WABOT-2",
        "ASIMO",
        "QRIO",
        "Honda E-Series",
        "Honda P-Series"
      ]
    },
    {
      "label": "Research legacy platforms",
      "robots": [
        "HRP-2",
        "HRP-3",
        "HRP-4",
        "HRP-4C",
        "HOAP-1",
        "HOAP-2",
        "HOAP-3",
        "WABIAN",
        "KOBIAN"
      ]
    },
    {
      "label": "Android and human-interaction robots",
      "robots": [
        "Actroid",
        "Geminoid HI-1",
        "Pepper"
      ]
    },
    {
      "label": "Japan-linked but not fully Japanese-origin",
      "robots": [
        "Pepper"
      ]
    }
  ],
  "whyJapanMatters": [
    "Japan’s importance comes from decades of work on bipedal walking, balance, human-scale robot design and real humanoid control problems.",
    "Waseda gave the field early full-scale humanoid projects. Honda turned bipedal research into a global public icon through ASIMO. AIST, Kawada and Kawasaki built research platforms that let labs test whole-body control and contact-rich tasks.",
    "Toyota’s T-HR3 shows the importance of teleoperation. JSK Lab’s Musashi line shows why body architecture still matters. Osaka University and Hiroshi Ishiguro’s android work shows that humanoid robotics also includes appearance, presence and communication."
  ],
  "limitations": [
    "Many Japanese humanoids were research platforms, not mass-market products.",
    "ASIMO, QRIO and Pepper became famous, but they did not become large-scale general-purpose workers.",
    "Bipedal humanoids still face hard problems in cost, battery life, manipulation, safety, reliability and deployment.",
    "Teleoperated robots like T-HR3 are important, but teleoperation is not the same as independent autonomous work.",
    "Androids like Actroid and Geminoid are important for interaction research, but they are not industrial humanoid workers."
  ],
  "relatedLinks": [
    {
      "label": "Humanoid robots guide",
      "path": "/robot-types/humanoid-robots"
    },
    {
      "label": "French humanoid robots",
      "path": "/humanoid-robots/france"
    },
    {
      "label": "Robotics companies",
      "path": "/robotics-companies"
    },
    {
      "label": "Humanoid data collection",
      "path": "/robotics-data-collection"
    },
    {
      "label": "Robotics software",
      "path": "/robotics-software"
    },
    {
      "label": "Robotics news",
      "path": "/robotics-news"
    }
  ]
};
